import axios from "axios";
import { getCookie, setCookie } from "./cookieHandler";
import moment from "moment-timezone";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function extractParameterValues(arr, paramName) {
  if (
    !Array.isArray(arr) ||
    arr.length === 0 ||
    typeof paramName !== "string"
  ) {
    return [];
  }

  return arr.map((item) => {
    if (item.hasOwnProperty(paramName)) {
      return String(item[paramName]);
    } else {
      return ""; // Return an empty string if the parameter doesn't exist in the object
    }
  });
}

export function areArraysEqual(arr1, arr2) {
  // Check if both arguments are arrays
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false;
  }

  // Check if the arrays have the same length
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Compare each object in the arrays
  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i];
    const obj2 = arr2[i];

    // Check if both elements are objects
    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
      return false;
    }

    // Check if the objects have the same number of properties
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
      return false;
    }

    // Check if the properties and values are the same
    for (const key in obj1) {
      if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
        if (obj1[key] !== obj2[key]) {
          return false;
        }
      } else {
        return false;
      }
    }
  }

  // If all checks pass, the arrays are considered equal
  return true;
}

export function findChangedAndSimilarObjects(
  arr1,
  arr2,
  searchProperty,
  nameProperty,
  alternativeSearchProperty
) {
  // Check if both arguments are arrays
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return { changedObjects: [], similarObjects: [] };
  }

  // Initialize arrays to store changed and similar objects
  const changedObjects = [];
  const similarObjects = [];

  // Find objects in arr2 that match the search criteria
  arr2.forEach((obj2) => {
    // Check if the identifier property is present in both objects using the primary search property
    if (obj2.hasOwnProperty(searchProperty)) {
      // Find corresponding object in arr1 based on the primary search property value
      const matchingObj1 = arr1.find(
        (obj1) => obj1[searchProperty] === obj2[searchProperty]
      );

      // If matchingObj1 is found, check if the name property has changed
      if (matchingObj1) {
        // Check if the name property is present in both objects
        if (
          matchingObj1.hasOwnProperty(nameProperty) &&
          obj2.hasOwnProperty(nameProperty)
        ) {
          if (matchingObj1[nameProperty] !== obj2[nameProperty]) {
            // Object has changed
            changedObjects.push(obj2);
          } else {
            // Object is similar
            // Check if it is not already added to changedObjects
            similarObjects.push(obj2);
          }
        }
      } else {
        // If matchingObj1 is not found, the object is additional in arr2
        changedObjects.push(obj2);
      }
    } else if (
      alternativeSearchProperty &&
      obj2.hasOwnProperty(alternativeSearchProperty)
    ) {
      // If the primary searchProperty is not found, try the alternativeSearchProperty
      const matchingObj1Alt = arr1.find(
        (obj1) =>
          obj1[alternativeSearchProperty] === obj2[alternativeSearchProperty]
      );

      if (matchingObj1Alt) {
        // Check if the name property is present in both objects
        if (
          matchingObj1Alt.hasOwnProperty(nameProperty) &&
          obj2.hasOwnProperty(nameProperty)
        ) {
          if (matchingObj1Alt[nameProperty] !== obj2[nameProperty]) {
            // Object has changed
            changedObjects.push(obj2);
          } else {
            // Object is similar
            // Check if it is not already added to changedObjects
            similarObjects.push(obj2);
          }
        }
      } else {
        // If matchingObj1Alt is not found, the object is additional in arr2
        changedObjects.push(obj2);
      }
    } else {
      // If neither searchProperty nor alternativeSearchProperty is found, the object is additional in arr2
      changedObjects.push(obj2);
    }
  });

  return { changedObjects, similarObjects };
}

export function findChangedObjects(arr1, arr2, searchProperty, nameProperty) {
  // Check if both arguments are arrays
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return [];
  }

  // Find objects in arr2 that match the search criteria
  const changedObjects = arr2.filter((obj2) => {
    // Find corresponding object in arr1 based on the search property value
    const matchingObj1 = arr1.find(
      (obj1) => obj1[searchProperty] === obj2[searchProperty]
    );

    // If matchingObj1 is found, check if the name property has changed
    if (matchingObj1) {
      return matchingObj1[nameProperty] !== obj2[nameProperty];
    }

    // If matchingObj1 is not found, the object is additional in arr2
    return true;
  });

  return changedObjects;
}

export function removeTrailingZeros(input, decimal) {
  const floatNumber = parseFloat(input) || 0;
  const containsAZ = containsAlphabetOrSymbol(input);

  if (isNaN(floatNumber) && decimal) {
    return parseFloat(0).toFixed(decimal);
  }

  if (isNaN(floatNumber) || containsAZ) {
    return input === true ? "True" : input === false ? "False" : input; // Return an error message for invalid input
  }

  if (
    isNaN(floatNumber) &&
    (input === "" || input === undefined || input === null) &&
    !decimal
  ) {
    return "";
  }

  const formattedFloat = decimal ? floatNumber.toFixed(decimal) : floatNumber; // Use a sufficiently large number of decimal places
  //const trimmedFloat = formattedFloat.replace(/\.?0+$/, ''); // Remove trailing zeros and the decimal point if there are only zeros

  return formattedFloat;
}

const containsAlphabet = (value) => {
  // Regular expression to match alphabetic characters
  const alphabetRegex = /[a-zA-Z]/;
  return alphabetRegex.test(value);
};

const containsAlphabetOrSymbol = (value) => {
  // Regular expression to match alphabetic characters or symbols
  const regex = /[a-zA-Z!@#$%^&*()_+{}\[\]:;"'<>,.?/|\\`~-]/;
  return regex.test(value);
};
function findItemByWhere(array, where) {
  return array.find((item) =>
    Object.entries(where).every(([key, value]) => {
      // Support nested keys for value (e.g., branch: { code: "LB" })
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return (
          item[key] != null &&
          Object.entries(value).every(
            ([subKey, subValue]) => item[key][subKey] === subValue
          )
        );
      }
      return item[key] === value;
    })
  );
}
export function formatInput(row, column, showValue, allowTime, branchCode) {
  //const hasDeepDive = column.subvalue ? column.subvalue.includes('.') : false;
  let input;
  const decimalCount =
    column != null && column.decimalCount != null ? column.decimalCount : 2;
  if (!column || !row) {
    return "Error";
  }
  if (typeof row === "object") {
    const dispField = showValue ? column?.showValue : column?.subvalue;
    input = row[column.field];
    if (typeof input === "number") {
      input = input.toFixed(decimalCount);
    }

    if (dispField) {
      const propParts = dispField.split(".");

      for (const part of propParts) {
        if (input && input.hasOwnProperty(part)) {
          input = input[part];
        } else {
          input = ""; // Treat as a string
        }
      }
    }
    if (
      (input === undefined || input === "" || input === null) &&
      column.secondaryField
    ) {
      input = formatInput(column.secondaryField);
    }
    if (column.isArray === true && column.showValue != null) {
      const arrayItemToShow =
        column.showValue === "first"
          ? 0
          : column.showValue === "last"
          ? row[column.field]?.length - 1
          : 0;
      if (
        row !== null &&
        row[column.field] != null &&
        row[column.field][arrayItemToShow]
      ) {
        input = row[column.field][arrayItemToShow][column.subvalue];
      } else {
        input = "";
      }
    }

    if (column.where != null) {
      const rowValue = row[column.field];
      if (rowValue != null && rowValue.length > 0) {
        const itemFound = findItemByWhere(rowValue, column.where);
        if (itemFound != null) {
          if (itemFound[column.subvalue] === 0) {
            input = "0.00"; // Handle zero case
          } else {
            input = itemFound[column.subvalue];
          }
        } else {
          input =
            column.decimalCount != null
              ? (0).toFixed(column.decimalCount)
              : "";
        }
      } else {
        input =
          column.decimalCount != null ? (0).toFixed(column.decimalCount) : "";
      }
    }
  } else {
    input = row;
  }

  // Check if the input matches the "yyyy-MM-dd" or "yyyy-MM-ddTHH:mm:ss" or "yyyy-MM-ddTHH:mm:ss.SSSZ" date format
  const dateRegex =
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})?)?$/;

  const branchTimezones = {
    AE: "Asia/Dubai",
    LB: "Asia/Beirut",
  };

  function formatDate(input, allowTime, branchCode) {
    if (!input) return null;

    const timeZone = branchTimezones[branchCode] || "UTC";
    const formattedDate = moment.utc(input).tz(timeZone);

    return allowTime
      ? formattedDate.format("DD/MM/YYYY HH:mm")
      : formattedDate.format("DD/MM/YYYY");
  }
  if (dateRegex.test(input) === true) {
    return formatDate(input, false, branchCode);
  }
  // If it's not a date, call the function to remove trailing zeros from a float
  return removeTrailingZeros(input, column.decimalCount) || "";
}

export function formatInput2(input, allowTime) {
  if (Array.isArray(input) || typeof input === "object") {
    return "Error";
  }
  // Check if the input matches the "yyyy-MM-dd" or "yyyy-MM-ddTHH:mm:ss" or "yyyy-MM-ddTHH:mm:ss.SSSZ" date format
  const dateRegex =
    /^(\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{3})?)?(?:Z)?)?)$/;
  if (dateRegex.test(input)) {
    const match = input.match(dateRegex);
    if (match) {
      const datePart = match[1];
      if (datePart.includes("T")) {
        const dateObj = new Date(datePart);
        const day = dateObj.getUTCDate().toString().padStart(2, "0");
        const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, "0");
        const year = dateObj.getUTCFullYear();
        const hours = dateObj.getUTCHours().toString().padStart(2, "0");
        const minutes = dateObj.getUTCMinutes().toString().padStart(2, "0");
        return `${day}/${month}/${year} ${
          allowTime ? `${hours}:${minutes}` : ""
        }`;
      } else {
        const [year, month, day] = datePart.split("-");
        return `${day}/${month}/${year}`;
      }
    }
  }

  // If it's not a date, call the function to remove trailing zeros from a float
  return removeTrailingZeros(input);
}

export function mapObjectValues(object1, object2) {
  const result = {};

  for (const key in object1) {
    if (Object.hasOwnProperty.call(object1, key)) {
      const sourceKey = object1[key];
      const keys = sourceKey.split(".");

      let value = object2;

      for (const subKey of keys) {
        if (value && subKey in value) {
          value = value[subKey];
        } else {
          value = undefined;
          break;
        }
      }

      result[key] = value;
    }
  }

  return result;
}

export function calculateDateDiff(
  pickupDate,
  returnDate,
  precision = 2,
  withoutAbs
) {
  const matchDates = checkIfDate(pickupDate) && checkIfDate(returnDate);
  if (matchDates) {
    const d1 = DateTime.fromISO(pickupDate.trim(), { zone: "utc" });
    const d2 = DateTime.fromISO(returnDate.trim(), { zone: "utc" });
    if (!d1.isValid || !d2.isValid) return 0;

    const ms1 = d1.toMillis();
    const ms2 = d2.toMillis();
    const diffTime = withoutAbs === true ? ms2 - ms1 : Math.abs(ms2 - ms1);

    const diffInDays = diffTime / (1000 * 60 * 60 * 24);
    return Number(diffInDays.toFixed(precision));
  }
  return 0;
}

export function fromUtcToBranchTime(utcTime, BranchCode) {
  const timeZone = BranchCode === "LB" ? "Asia/Beirut" : "Asia/Dubai";

  // Create a DateTime object from the UTC time string
  const utcDateTime = DateTime.fromISO(utcTime, { zone: "utc" });

  // Convert the UTC DateTime to the specified time zone
  const localDateTime = utcDateTime.setZone(timeZone);

  return localDateTime;
}

export function calculateDateDiffWithGrace(
  pickupDate,
  returnDate,
  grace_period
) {
  // Validate the dates
  let matchDates = checkIfDate(pickupDate) && checkIfDate(returnDate);
  if (matchDates) {
    const date1 = new Date(pickupDate.trim());
    const date2 = new Date(returnDate.trim());

    // Calculate the total difference in milliseconds
    const diffTime = date2 - date1; // Can be negative if pickupDate is later
    if (diffTime < 0) return 0; // Return 0 for invalid intervals (pickup is after return)

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Total full days
    const remainingHours =
      (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60); // Remaining hours after full days

    // Include the 4-hour grace period]
    let gp = grace_period;

    if (grace_period == null) gp = 4;
    if (remainingHours > gp) {
      return diffDays + 1; // Add an extra day if remaining hours exceed the grace period
    } else {
      return diffDays; // Otherwise, return the full days only
    }
  }

  // Fallback for invalid input
  return 0;
}

const checkIfDate = (date) => {
  // Check if the input matches the "yyyy-MM-dd" or "yyyy-MM-ddTHH:mm:ss" or "yyyy-MM-ddTHH:mm:ss.SSSZ" date format
  const dateRegex =
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[-+]\d{2}:\d{2})?)$/;

  if (dateRegex.test(date)) {
    return true; // Return true if the input matches the regex
  }

  return false;
};
export function removeTrailingZerosUntil2(inputNumber, decimalCount, isDec) {
  const numStr = parseFloat(inputNumber);

  if (isDec && isNaN(numStr)) {
    return "0.00";
  }

  if (isNaN(numStr)) {
    return inputNumber;
  }
  // Convert the number to a string
  let numberString = inputNumber?.toString();

  // Check if the number has more than 2 decimals
  if (!isNaN(parseInt(decimalCount))) {
    return parseFloat(inputNumber).toFixed(decimalCount);
  } else {
    if (numberString.includes(".") && numberString.split(".")[1].length > 2) {
      // If more than 2 decimals, round to 2 decimals
      return parseFloat(inputNumber).toFixed(2);
    } else {
      // If 2 or fewer decimals, return the original number
      return inputNumber;
    }
  }
}

// export function findDataInFields(
//   arrayOfObjects,
//   searchString,
//   returnNullIfNull
// ) {
//   // Split the searchString to get the object property and the key to search
//   const [objectName, key] = searchString.split(".");

//   // Find the object with the specified name in the array
//   const foundObject = arrayOfObjects.find((obj) => obj.name === objectName);

//   // If the object is found and it has a 'data' property which is an array
//   if (key === "defaultValue") {
//     const defaultValue = foundObject[key];
//     if (defaultValue != null) {
//       return foundObject[key];
//     } else {
//       if (returnNullIfNull === true) {
//         return null;
//       }
//       return false;
//     }
//   }
//   if (key === "changed") {
//     const changed = foundObject[key];
//     if (changed != null) {
//       return foundObject[key];
//     } else {
//       return false;
//     }
//   }

//   if (foundObject && foundObject.data && Array.isArray(foundObject.data)) {
//     // Find the object within the 'data' array with the specified id
//     const foundDataObject = foundObject.data.find(
//       (dataObj) => dataObj.id === foundObject.defaultValue
//     );

//     // If the object is found, return the value of the specified key

//     if (foundDataObject && foundDataObject[key]) {
//       // console.log(foundDataObject[key], foundDataObject, key);
//       return foundDataObject[key];
//     }
//   } else if (
//     foundObject &&
//     (foundObject.defaultValue || foundObject.defaultValue === 0)
//   ) {
//     return typeof foundObject.defaultValue === "object"
//       ? foundObject.defaultValue[key]
//       : foundObject.defaultValue;
//   }
//   if (returnNullIfNull != null) {
//     return null;
//   }
//   // Return null if not found
//   return "";
// }

export function findDataInFields(
  arrayOfObjects,
  searchString,
  returnNullIfNull
) {
  // Split the searchString to get the path components
  const pathComponents = searchString.split(".");

  // Handle cases where path has 3 components (objectName.key.subKey)
  if (pathComponents.length === 3) {
    const [objectName, key, subKey] = pathComponents;

    // Find the object with the specified name in the array
    const foundObject = arrayOfObjects.find((obj) => obj.name === objectName);

    if (!foundObject) {
      return returnNullIfNull ? null : "";
    }

    // Handle special cases for 'defaultValue' and 'changed' at level 2
    if (key === "defaultValue") {
      const defaultValue = foundObject[key];
      if (defaultValue != null) {
        // If there's a subKey, try to access it on the defaultValue object
        if (
          subKey &&
          typeof defaultValue === "object" &&
          defaultValue !== null
        ) {
          return defaultValue[subKey] ?? (returnNullIfNull ? null : "");
        }
        return defaultValue;
      }
      return returnNullIfNull ? null : false;
    }

    if (key === "changed") {
      const changed = foundObject[key];
      if (changed != null) {
        // If there's a subKey, try to access it on the changed object
        if (subKey && typeof changed === "object" && changed !== null) {
          return changed[subKey] ?? (returnNullIfNull ? null : "");
        }
        return changed;
      }
      return false;
    }

    // Handle data array case
    if (foundObject.data && Array.isArray(foundObject.data)) {
      const foundDataObject = foundObject.data.find(
        (dataObj) => dataObj.id === foundObject.defaultValue
      );

      if (foundDataObject && foundDataObject[key]) {
        // If there's a subKey, try to access it on the found data object's property
        if (
          subKey &&
          typeof foundDataObject[key] === "object" &&
          foundDataObject[key] !== null
        ) {
          return foundDataObject[key][subKey] ?? (returnNullIfNull ? null : "");
        }
        return foundDataObject[key];
      }
    }

    // Handle defaultValue case for nested path
    if (foundObject.defaultValue || foundObject.defaultValue === 0) {
      if (
        typeof foundObject.defaultValue === "object" &&
        foundObject.defaultValue !== null
      ) {
        if (foundObject.defaultValue[key]) {
          const nestedValue = foundObject.defaultValue[key];
          // If there's a subKey, try to access it on the nested value
          if (
            subKey &&
            typeof nestedValue === "object" &&
            nestedValue !== null
          ) {
            return nestedValue[subKey] ?? (returnNullIfNull ? null : "");
          }
          return nestedValue;
        }
      }
      return foundObject.defaultValue;
    }

    return returnNullIfNull ? null : "";
  }

  // Original handling for paths with 2 components (maintain backward compatibility)
  const [objectName, key] = pathComponents;

  // Find the object with the specified name in the array
  const foundObject = arrayOfObjects.find((obj) => obj.name === objectName);

  // If the object is found and it has a 'data' property which is an array
  if (key === "defaultValue") {
    const defaultValue = foundObject[key];
    if (defaultValue != null) {
      return foundObject[key];
    } else {
      if (returnNullIfNull === true) {
        return null;
      }
      return false;
    }
  }
  if (key === "changed") {
    const changed = foundObject[key];
    if (changed != null) {
      return foundObject[key];
    } else {
      return false;
    }
  }

  if (foundObject && foundObject.data && Array.isArray(foundObject.data)) {
    // Find the object within the 'data' array with the specified id
    const foundDataObject = foundObject.data.find(
      (dataObj) => dataObj.id === foundObject.defaultValue
    );

    // If the object is found, return the value of the specified key
    if (foundDataObject && foundDataObject[key]) {
      return foundDataObject[key];
    } else return null;
  } else if (
    foundObject &&
    (foundObject.defaultValue || foundObject.defaultValue === 0)
  ) {
    return typeof foundObject.defaultValue === "object"
      ? foundObject.defaultValue[key]
      : foundObject.defaultValue;
  }
  if (returnNullIfNull != null) {
    return null;
  }
  // Return null if not found
  return "";
}

export function extractFromObject(obj, searchString) {
  // Split the search string into parts
  const keys = searchString.split(".");

  // Initialize a variable to hold the current value
  let currentValue = obj;

  // Traverse the object
  for (const key of keys) {
    // Check if the current value is an object and has the key
    if (
      typeof currentValue === "object" &&
      currentValue !== null &&
      key in currentValue
    ) {
      // Update the current value to the nested object
      currentValue = currentValue[key];
    } else {
      // If the key doesn't exist, return the previous valid value
      return currentValue;
    }
  }

  // Return the value found at the end of the traversal
  return currentValue;
}

export function findDataInObject(_object, searchString) {
  // Split the searchString to get the object property and the key to search
  const [objectName, key] = searchString.split(".");

  if (_object[objectName]) {
    if (key && typeof _object[objectName] === "object") {
      return _object[objectName][key];
    } else {
      return _object[objectName];
    }
  }

  // Return null if not found
  return "";
}

export async function fetchDataAndMapToField(
  config,
  apiUrl,
  fieldName,
  temp,
  nameField,
  subnameField,
  concat,
  subnameField2,
  subnameField3,
  subnameField4
) {
  try {
    const response = await axios.get(
      `${config.apiUrl}/api/${apiUrl}?pagination=false`,
      {
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
        },
      }
    );
    if (response.headers?.authorization) {
      if (`Bearer ${getCookie("_AT")}` !== response.headers.authorization) {
        setCookie("_AT", response.headers.authorization.split(" ")[1]);
      }
    }

    if (response.data && response.data.length > 0) {
      const dataList = response.data.map((item) => {
        const concatVal = Array.isArray(concat)
          ? concat.map((e) => (item[e] ? item[e] : ""))
          : null;
        const concatStr = concatVal ? concatVal.join(" ") : "";

        return {
          id: item.id,
          name: concatStr || item[nameField ?? "name"],
          ...(subnameField && { subname: item[subnameField ?? "name"] }),
          ...(subnameField && { [subnameField]: item[subnameField ?? "name"] }),
          ...(subnameField2 && {
            [subnameField2]: item[subnameField2 ?? "name"],
          }),
          ...(subnameField3 && {
            [subnameField3]: item[subnameField3 ?? "name"],
          }),
          ...(subnameField4 && {
            [subnameField4]: item[subnameField4 ?? ""],
          }),
        };
      });
      const fieldIndex = temp.findIndex((field) => field.name === fieldName);
      if (fieldIndex !== -1) {
        let temppp = { ...temp[fieldIndex], data: dataList };
        temp[fieldIndex] = temppp;
      }
    }
  } catch (error) {
    // Handle the error here if needed
    console.error(`Error fetching data for ${fieldName}:`, error);
  }
}

export const addFilters = (currentFields, filters) => {
  if (filters && currentFields) {
    let concatFilters = "";
    for (const key in filters) {
      if (Object.hasOwnProperty.call(filters, key)) {
        const element = filters[key];
        if (element.includes("<s>")) {
          concatFilters += key + "=" + element.replace("<s>", "") + "&";
        } else if (element.includes("<now>")) {
          concatFilters += key + "=" + new Date().toISOString() + "&";
        } else {
          concatFilters +=
            key +
            "=" +
            encodeURIComponent(findDataInFields(currentFields, element)) +
            "&";
        }
      }
    }

    return concatFilters;
  }

  return "";
};

export async function getFromAPI(config, apiUrl) {
  try {
    const response = await axios.get(`${config.apiUrl}/api/${apiUrl}`, {
      headers: {
        Authorization: `Bearer ${getCookie("_AT")}`,
      },
    });
    if (response.headers?.authorization) {
      if (`Bearer ${getCookie("_AT")}` !== response.headers.authorization) {
        setCookie("_AT", response.headers.authorization.split(" ")[1]);
      }
    }
    return response;
  } catch (error) {
    console.error(`Error fetching data for ${apiUrl}:`, error);
    return null;
  }
}

export async function fetchDataToDropDown(
  config,
  apiUrl,
  nameField,
  subnameField,
  concat,
  concatBy,
  _filters,
  lazyloadParamName,
  returnDataAsIs,
  subnameField2,
  nameTransformer
) {
  try {
    // add query const to differenciate between regular api calls and ones with pagination
    const response = await axios.get(
      `${config.apiUrl}/api/${apiUrl}${
        _filters ? "?" + _filters + "pagination=false" : "?pagination=false"
      }`,
      {
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
        },
      }
    );
    if (response.headers?.authorization) {
      if (`Bearer ${getCookie("_AT")}` !== response.headers.authorization) {
        setCookie("_AT", response.headers.authorization.split(" ")[1]);
      }
    }
    if (response.data && response.data.length > 0) {
      const uniqueBranches = new Set();
      if (lazyloadParamName != null) {
        response.data.forEach((item) => {
          const name = item[nameField][lazyloadParamName];
          const coverageId = item[nameField].id;
          // Add the branch information to the Set as a string
          uniqueBranches.add(`${coverageId}:${name}`);
        });
        const result = Array.from(uniqueBranches).map((branch) => {
          const [id, name] = branch.split(":");
          return { id: parseInt(id), name }; // Convert id back to a number
        });
        return result;
      } else {
        const dataList = response.data.map((item, i) => {
          let name = "";
          if (concat === true) {
            name =
              item[nameField ?? "name"] +
              concatBy +
              item[subnameField ?? "name"];
          } else {
            name = item[nameField ?? "name"];
          }
          if (typeof nameTransformer === "function") {
            name = nameTransformer(name, item);
          }
          return {
            id:
              lazyloadParamName != null
                ? item[nameField ?? "name"].id
                : item.id,
            name: name,
            subname: item[subnameField ?? "name"],
            [nameField || "unknown"]: item[nameField ?? "name"],
            [subnameField || "unknown"]: item[subnameField ?? "name"],
            [subnameField2 || "unknown"]: item[subnameField2 ?? "name"],
          };
        });
        return dataList;
      }
    }
    if (returnDataAsIs === true && response.data.length === 0) {
      return response.data;
    }
  } catch (error) {
    // Handle the error here if needed
    console.error(`Error fetching data for ${nameField}:`, error);
  }
}

export function formatDateToDDMMYYYY(date) {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
export function formatDateToYYYYMMDD(date) {
  if (date === null || date === undefined || date === "") {
    return null;
  }

  // const year = date.getFullYear();
  // const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  // const day = String(date.getDate()).padStart(2, '0');

  //return `${year}-${month}-${day}`;
  return date.toISOString();
}

export function changeDateFormYYYYMMDDToDDMMYYYY(inputDateStr) {
  const [year, month, day] = inputDateStr.split("-").map(Number);

  // Month in JavaScript's Date object is 0-based, so add 1 to the month
  const formattedDate = `${day}/${month + 1}/${year}`;

  return formattedDate;
}
export function createDateFromYYYYMMDD(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);

  // Month in JavaScript's Date object is 0-based, so subtract 1 from the month
  return new Date(year, month - 1, day);
}
export function createDateFromDDMMYYYY(dateString) {
  // Split the input string by the '/' separator
  const parts = dateString.split("/");

  // Extract day, month, and year components
  const day = parseInt(parts[0], 10); // Convert to integer
  const month = parseInt(parts[1], 10) - 1; // Months are zero-based, so subtract 1
  const year = parseInt(parts[2], 10);

  // Create a new Date object with the extracted components
  const dateObject = new Date(year, month, day);

  return dateObject;
}
export function addDefaultValuesToFields(fields, defaultValuesObject) {
  // Convert the default values object into an array of key-value pairs
  const defaultValuesArray = Object.entries(defaultValuesObject);

  // Loop through the fields array and add the defaultValue parameter conditionally
  const fieldsWithDefaultValue = fields.map((field) => {
    const defaultValueEntry = defaultValuesArray.find(
      ([paramName]) => paramName === field.name
    );
    let defaultValue = defaultValueEntry ? defaultValueEntry[1] : undefined;
    if (defaultValue == null && field.subname != null) {
      const defaultValueEntryBySubname = defaultValuesArray.find(
        ([paramName]) => paramName === field.subname
      );
      defaultValue = defaultValueEntryBySubname
        ? defaultValueEntryBySubname[1]
        : undefined;
    }
    return {
      ...field,
      defaultValue: defaultValue,
    };
  });

  //Check for init Conditions
  const DisableFieldConditions = fieldsWithDefaultValue.filter(
    (x) => x.disableCondition !== undefined && x.disableCondition !== null
  );
  let paramsToDisable = [];
  let paramsToEnable = [];
  if (
    Array.isArray(DisableFieldConditions) &&
    DisableFieldConditions.length > 0
  ) {
    DisableFieldConditions.forEach((Fieldcondition) => {
      if (
        Array.isArray(Fieldcondition.disableCondition) &&
        Fieldcondition.disableCondition.length > 0
      ) {
        for (const condition of Fieldcondition.disableCondition) {
          if (Fieldcondition.defaultValue === condition.value) {
            if (
              Array.isArray(condition.params) &&
              condition.params.length > 0
            ) {
              paramsToDisable = [...paramsToDisable, ...condition.params];
            }
            if (
              Array.isArray(condition.showparams) &&
              condition.showparams.length > 0
            ) {
              paramsToEnable = [...paramsToEnable, ...condition.showparams];
            }
            break;
          }
        }
      }
    });

    fieldsWithDefaultValue.forEach((field) => {
      if (paramsToDisable.includes(field.name)) {
        field.disabled = true;
        field.isRequired = false;
      }
      if (paramsToEnable.includes(field.name)) {
        field.disabled = false;
        field.isRequired = true;
      }
    });
  }
  return fieldsWithDefaultValue;
}

export function searchObjects(searchInput, arrayOfObjects) {
  // Use the filter method to create a new array of objects that match the search criteria
  const result = arrayOfObjects.filter((object) => {
    // Iterate over the object's values and check if any of them include the search input
    for (const key in object) {
      const value = object[key];
      if (typeof value === "string" || typeof value === "number") {
        if (value.toString().includes(searchInput.toString())) {
          return true; // Object contains the search input
        }
      }
    }
    return false; // Object does not contain the search input
  });

  return result;
}

export async function addAddiotionDrivertoExclude(
  config,
  temp,
  fieldName,
  limitFieldName
) {
  const resp = await axios
    .get(config.apiUrl + "/api/general", {
      headers: { Authorization: `Bearer ${getCookie("_AT")}` },
    })
    .catch((e) => console.log("Error: ", e.message));

  if (Array.isArray(resp?.data) && resp?.data?.length > 0) {
    const additionalDriverAddonId = resp.data[0].additional_driver_addon.id;
    const newExclude = [
      {
        name: "addon.id",
        value: additionalDriverAddonId,
      },
      {
        name: "delete",
        value: true,
      },
    ];

    const fieldIndex = temp.findIndex((field) => field.name === fieldName);
    if (fieldIndex !== -1) {
      let temppp = { ...temp[fieldIndex], exclude: newExclude };
      temp[fieldIndex] = temppp;
    }

    const newLimit = resp.data[0].additional_driver_addon.limit;

    if (limitFieldName) {
      const limitFieldIndex = temp.findIndex(
        (field) => field.name === limitFieldName
      );
      if (limitFieldIndex !== -1) {
        let temppp = { ...temp[limitFieldIndex], limit: newLimit || 3 };
        temp[limitFieldIndex] = temppp;
      }
    }
  }
}

export function extractDataOO(originalData, toShow) {
  const results = [];

  for (const item of originalData) {
    const result = {};
    if (item.new) {
      result["new"] = true;
    }
    for (const key in toShow) {
      if (toShow.hasOwnProperty(key)) {
        const properties = toShow[key]
          .split(",")
          .map((property) => property.trim());
        let concatenatedValue = "";
        let insertSpace = false; // To track when to insert a space

        for (const prop of properties) {
          if (prop.trim() === "") {
            insertSpace = true;
            continue;
          }

          if (insertSpace) {
            concatenatedValue += " "; // Insert a space
            insertSpace = false; // Reset the flag
          }

          const propParts = prop.split(".");

          let value = item;

          for (const part of propParts) {
            if (value && value.hasOwnProperty(part)) {
              value = value[part];
            } else {
              if (part.includes("<s>")) {
                value = part.replace("<s>", "");
              } else if (part.includes("<d-lb>")) {
                const dateKey = part.replace("<d-lb>", "");
                const dateValue = value[dateKey];

                if (dateValue && isValidDate(dateValue)) {
                  value = formatDateWithTime(dateValue, "LB");
                } else {
                  value = "Timestamp will be set after save";
                }
              } else if (part.includes("<date>")) {
                const dateKey = part.replace("<date>", "");
                const dateValue = value[dateKey];

                if (dateValue && isValidDate(dateValue)) {
                  value = formatDate(dateValue);
                } else {
                  value = "Timestamp will be set after save";
                }
              } else if (part.includes("+")) {
                const split = part.split("+");
                // split will consist of keys that are numeric values , loop on keys and set value as added values
                let totalNum = 0;
                split.forEach((k) => {
                  const num = value[k] ? parseFloat(value[k]) : 0;
                  totalNum += num;
                });
                value = totalNum;
              } else {
                value = "";
              }
            }
          }

          concatenatedValue += value;
        }

        result[key] = concatenatedValue;
      }
    }

    results.push(result);
  }

  return results;
}
export function isValidDate(date) {
  return !isNaN(new Date(date).getTime());
}
export function isValidDDMMYYYYhhmmA(dateString) {
  // Regex to match DD/MM/YYYY hh:mm AM/PM (24-hour clock not supported)
  const regex = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{1,2}):(\d{2}) (AM|PM)$/i;
  const match = dateString.match(regex);
  if (!match) return false;

  let [, day, month, year, hour, minute, ampm] = match;
  day = parseInt(day, 10);
  month = parseInt(month, 10);
  year = parseInt(year, 10);
  hour = parseInt(hour, 10);
  minute = parseInt(minute, 10);

  // Basic range checks
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 1 ||
    hour > 12 ||
    minute < 0 ||
    minute > 59
  )
    return false;

  // Check valid date (e.g., not 31/02/2025)
  const testDate = new Date(year, month - 1, day);
  if (
    testDate.getFullYear() !== year ||
    testDate.getMonth() !== month - 1 ||
    testDate.getDate() !== day
  )
    return false;

  // Passed all checks
  return true;
}
export function mergeArrays(a1, a2) {
  const result = [];

  // Step 1: Add objects from a1 that have a matching id in a2
  for (const objA1 of a1) {
    const matchingObjA2 = a2.find((objA2) => objA2.id == parseInt(objA1.id));
    if (matchingObjA2) {
      result.push(objA1);
    }
  }

  // Step 2: Add objects from a2 that have an id not included in a1
  for (const objA2 of a2) {
    const isIdNotInA1 = !a1.some((objA1) => objA1.id == parseInt(objA2.id));
    if (isIdNotInA1) {
      result.push(objA2);
    }
  }

  // Step 3: Add all remaining objects from a1
  for (const objA1 of a1) {
    const hasMatchingObjA2 = a2.some(
      (objA2) => objA2.id === parseInt(objA1.id)
    );
    if (!hasMatchingObjA2) {
      result.push(objA1);
    }
  }

  return result;
}

export function getDifferingValues(fields, formData, isFormData) {
  const differingValues = isFormData ? new FormData() : {};

  for (const field of fields) {
    const fieldName = field.name;
    //const defaultValue = field.defaultValue;
    const isChanged = field.changed;
    let formDataValue = formData[fieldName];

    if (isFormData) {
      formDataValue =
        field.type === "file"
          ? formData.getAll(fieldName)
          : formData.get(fieldName);
      // Use FormData.set() to set the value if isFormData is true
      if (formDataValue !== undefined && isChanged) {
        if (field.type === "file") {
          for (let i = 0; i < formDataValue.length; i++) {
            differingValues.append(fieldName, formDataValue[i]);
          }
        } else {
          differingValues.append(fieldName, formDataValue);
        }
      }
    } else {
      if (formDataValue !== undefined && isChanged) {
        differingValues[fieldName] = formDataValue;
      } else if (Array.isArray(formDataValue) && formDataValue.length > 0) {
        differingValues[fieldName] = formDataValue;
      }
    }
  }
  if (isFormData) {
    if (formData.get("deleteFiles")) {
      differingValues.append("deleteFiles", formData.get("deleteFiles"));
    }
  } else {
    if (formData.deleteFiles) {
      differingValues["deleteFiles"] = formData.deleteFiles;
    }
  }

  return differingValues;
}

export function createObjectFromArrayOfObjects(arrayOfObjects, propertyName) {
  const resultObject = {};

  for (const obj of arrayOfObjects) {
    if (obj.hasOwnProperty(propertyName)) {
      resultObject[obj[propertyName]] = obj;
    }
  }

  return resultObject;
}

export function hasFileInput(jsonData) {
  for (const key in jsonData) {
    if (jsonData.hasOwnProperty(key)) {
      const value = jsonData[key];
      // Check if the value is an object and contains a 'file' property
      if (typeof value === "object" && value.hasOwnProperty("file")) {
        return true; // Found a file input
      }
      // Check if the value is a FileList and if it contains files
      if (value instanceof FileList && value.length > 0) {
        return true; // Found a file input
      }
    }
  }
  return false; // No file input found
}
export function getObjectParamsWithSubstring(
  jsonData,
  substring,
  withoutSubstring
) {
  const matchingParams = {};
  for (const key in jsonData) {
    if (jsonData.hasOwnProperty(key) && key.includes(substring)) {
      const keyname = withoutSubstring ? key.replace(substring, "") : key;
      matchingParams[keyname] = jsonData[key];
    }
  }
  return matchingParams;
}

export function extractFiles(jsonFormData, fields) {
  const files = [];
  for (const key in jsonFormData) {
    if (jsonFormData[key] !== null && jsonFormData[key] !== undefined) {
      if (
        jsonFormData[key] instanceof FileList &&
        jsonFormData[key].length > 0
      ) {
        const findRelatedField = Array.isArray(fields)
          ? fields.find((x) => x.name === key)
          : false;
        const isMulti = findRelatedField
          ? findRelatedField.isMulti || false
          : jsonFormData[key].length > 1;
        const childRelated = findRelatedField
          ? findRelatedField.childRelated || false
          : false;
        const keySplit = childRelated ? key.split("_") : "";

        const childRelatedParam =
          keySplit && keySplit.length > 0 ? keySplit[0] : "";
        const childRelatedParamId = jsonFormData[childRelatedParam]
          ? typeof jsonFormData[childRelatedParam] === "object"
            ? jsonFormData[childRelatedParam].id
            : jsonFormData[childRelatedParam]
          : "";
        for (let i = 0; i < jsonFormData[key].length; i++) {
          if (jsonFormData[key][i] instanceof File) {
            files.push({
              param: key,
              value: jsonFormData[key][i],
              isMulti: isMulti,
              childRelated: childRelated,
              childRelatedId: childRelatedParamId,
            });
          }
        }
      }
    }
  }

  return files;
}

export function processFormData(formDataToSend, jsonFormData) {
  for (const key in jsonFormData) {
    if (jsonFormData[key] !== null && jsonFormData[key] !== undefined) {
      if (
        jsonFormData[key] instanceof FileList &&
        jsonFormData[key].length > 0
      ) {
        // Check if it's an array of File objects
        for (let i = 0; i < jsonFormData[key].length; i++) {
          if (jsonFormData[key][i] instanceof File) {
            formDataToSend.append(key, jsonFormData[key][i]);
          }
          // else {
          //   // Handle non-File data in the array if needed
          // }
        }
      } else if (
        Array.isArray(jsonFormData[key]) &&
        jsonFormData[key].length > 0
      ) {
        formDataToSend.append(key, JSON.stringify(jsonFormData[key]));
      } else if (typeof jsonFormData[key] === "object") {
        formDataToSend.append(key, JSON.stringify(jsonFormData[key]));
      } else {
        // Handle non-File data if needed
        formDataToSend.append(key, jsonFormData[key]);
      }
    }
  }
}

export const disabledIfFieldChecker = (
  currentFields,
  { field, valuesArray }
) => {
  const val = findDataInFields(currentFields, field);
  if (valuesArray.includes(val) === true) {
    return true;
  }
  return false;
};
export const fieldsNotEmptyChecker = (currentFields, fields) => {
  if (!Array.isArray(fields) || fields.length === 0) return false;
  if (!currentFields || typeof currentFields !== "object") return false;

  for (const field of fields) {
    const val = findDataInFields(currentFields, field);

    if (val !== null && val !== undefined && String(val).trim() !== "") {
      return true;
    }
  }

  return false;
};

export const disabledIfNotFieldChecker = (
  currentFields,
  { field, valuesArray }
) => {
  const val = findDataInFields(currentFields, field);

  if (val == null) return true;
  if (valuesArray.includes(val) === true) {
    return true;
  }
  return false;
};
export const isFieldEnabledChecker = (currentFields, { field, value }) => {
  const val = findDataInFields(currentFields, field);
  if (field === "auto_calculate") {
    console.log(val, "val");
  }
  if (value === val) {
    return true;
  }
  return false;
};
export function waitForCookie(name) {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      const value = getCookie(name);
      if (value) {
        clearInterval(interval);
        resolve(value);
      }
    }, 50);
  });
}

export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Create a mapping of href paths to proper display names for navigation
// This function searches in both main and settings navigation to find the correct name
export const getNavigationDisplayName = (
  pathname,
  currentNavigation,
  allNavigationArrays = null
) => {
  // Remove leading slash
  const cleanPath = pathname.startsWith("/") ? pathname.slice(1) : pathname;

  // Create a flat map of all navigation items (including sub-items)
  // Sub-items take precedence over parent items
  const createHrefToNameMap = (navItems) => {
    const map = {};

    navItems.forEach((item) => {
      // First add parent items (lower priority)
      if (item.href && item.href !== "#") {
        const cleanHref = item.href.startsWith("/")
          ? item.href.slice(1)
          : item.href;
        map[cleanHref] = item.name;
      }

      // Then add sub-items (higher priority - will override parent if same href)
      if (item.subItems) {
        item.subItems.forEach((subItem) => {
          if (subItem.href && subItem.href !== "#") {
            const cleanHref = subItem.href.startsWith("/")
              ? subItem.href.slice(1)
              : subItem.href;
            map[cleanHref] = subItem.name; // This will override parent if same href
          }
        });
      }
    });

    return map;
  };

  // Search in current navigation first
  let hrefToNameMap = createHrefToNameMap(currentNavigation);

  // Try exact match in current navigation
  if (hrefToNameMap[cleanPath]) {
    return hrefToNameMap[cleanPath];
  }

  // Try matching the first part of the path in current navigation (for nested routes)
  if (cleanPath.includes("/")) {
    const firstPart = cleanPath.split("/")[0];
    if (hrefToNameMap[firstPart]) {
      return hrefToNameMap[firstPart];
    }
  }

  // If not found and we have additional navigation arrays, search in all of them
  if (allNavigationArrays && Array.isArray(allNavigationArrays)) {
    for (const navArray of allNavigationArrays) {
      const additionalMap = createHrefToNameMap(navArray);

      // Try exact match
      if (additionalMap[cleanPath]) {
        return additionalMap[cleanPath];
      }

      // Try matching the first part of the path (for nested routes)
      if (cleanPath.includes("/")) {
        const firstPart = cleanPath.split("/")[0];
        if (additionalMap[firstPart]) {
          return additionalMap[firstPart];
        }
      }
    }
  }

  // Fallback to capitalize first letter if no match found
  return capitalizeFirstLetter(cleanPath || "Dashboard");
};

export function formatDateWithTime(utcTime, branchCode) {
  const timeZone = branchCode === "LB" ? "Asia/Beirut" : "Asia/Dubai";

  // Create DateTime object
  const utcDateTime = DateTime.fromISO(utcTime, { zone: "utc" });

  // Convert to specific timezone
  const localDateTime = utcDateTime.setZone(timeZone);

  // Format date as DD/MM/YYYY
  const formattedDate = localDateTime.toFormat("dd/MM/yyyy");

  // Keep the time simple
  const formattedTime = localDateTime.toLocaleString(DateTime.TIME_SIMPLE);

  return `${formattedDate} ${formattedTime}`;
}
export function formatDate(date) {
  // Create DateTime object
  const utcDateTime = DateTime.fromISO(date, { zone: "utc" });

  // Convert to local timezone (system timezone)
  const localDateTime = utcDateTime.toLocal();

  // Format date as DD/MM/YYYY only (without time)
  const formattedDate = localDateTime.toFormat("dd/MM/yyyy");

  return formattedDate;
}

export function resolveFieldValue(
  key,
  value,
  fields,
  id,
  consts = {},
  inputValue
) {
  if (value === "<id>") return id;

  // Generic constant: <const>constName
  if (typeof value === "string" && value.startsWith("<const>")) {
    const constName = value.slice(7);
    return consts.hasOwnProperty(constName) ? consts[constName] : undefined;
  }

  // Custom value: <value>anything
  if (typeof value === "string" && value.startsWith("<value>")) {
    return value.slice(7); // remove "<value>"
  }

  // Custom input value: <inputvalue>
  if (typeof value === "string" && value.startsWith("<inputvalue>")) {
    // Can extend to support <inputvalue>key if you want
    return inputValue;
  }

  // Fallback: find in fields
  return findDataInFields(fields, value, true);
}
export function getFirstDayOfMonthString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-01`;
}
export function getTodayString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
}
export function removeDeductFromName(name) {
  if (typeof name !== "string") return name;
  // Remove leading "DEDUCT" (case-insensitive), optional separators, and extra spaces
  // Examples matched: "DEDUCT Coverage", "deduct- Coverage", "DEDUCT:Coverage", " deduct   Coverage"
  return name.replace(/^\s*DEDUCT[:\-\s]+/i, "").trim();
}
